<template>
  <div></div>
</template>

<script>
import Moment from 'moment';
import MomentTimeZone from 'moment-timezone';
var Highcharts = require('highcharts');

// Load the boost module, and initialize it.
require('highcharts/modules/boost')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);
require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/draggable-points')(Highcharts);

export default {
  name: 'Chart',
  data() {
    return {
      chart: undefined,
      loaded: false,
      chartSeries: null,
      dragPointsArray: [],
      cloneOfChartSeries: null,
      isDataChangedByDrag: false,
    };
  },
  props: {
    seriesDetails: Array,
    redrawChart: Boolean,
    enableXAxiesLabels: {
      required: false,
    },
    enableYAxiesLabels: {
      required: false,
    },
    enableTooltip: {
      required: false,
    },
    enableAnimation: {
      required: false,
    },
    lineWidth: {
      required: false,
      default: 1,
    },
    lineColor: {
      required: false,
      default: '#adb9d2',
    },
    axisStyleFontSize: {
      default: '7px',
    },
    enableLegend: {
      default: false,
    },
    xAxisPlotBands: {
      type: Array,
      default: () => [],
    },
    calculateMetaData: {
      type: Function,
      default: () => {},
    },
    addNewSeries: {
      type: Function,
      default: () => {},
    },
    setAndCreateLogObject: {
      type: Function,
      default: () => {},
    },
  },
  methods: {
    setChart() {
      const vm = this;
      this.chart = Highcharts.chart(this.$el, {
        title: {
          text: '',
        },
        time: {
          getTimezoneOffset: function (timestamp) {
            var zone = 'Europe/Oslo',
              // eslint-disable-next-line no-undef
              timezoneOffset = -moment.tz(timestamp, zone).utcOffset();
            return timezoneOffset;
          },
        },
        chart: {
          type: 'line',
          zoomType: 'x',
          animation: this.enableAnimation,
          events: {
            load: function (event) {
              vm.checkAllowDraggingToChart(event.target, this);
            },
            selection: function (event) {
              let min, max;
              // Get min & max from xAxis
              if (event.hasOwnProperty('xAxis')) {
                [min, max] = vm.checkAllowDraggingToChart(event, this);
              } else {
                // reset values
                min = null;
                max = null;
                setTimeout(() => {
                  if (
                    vm.chartSeries.length === 1 &&
                    this.series[0].closestPointRangePx < 6
                  )
                    vm.removeDraggingToChart();
                }, 500);
              }
              // Emit to parent
              vm.$emit('setSelectionRange', min, max);
            },
          },
          resetZoomButton: {
            position: {
              align: 'right', // by default
              x: 0,
              y: -40,
            },
          },
          marginTop: 50,
        },
        credits: {
          enabled: false,
        },
        tooltip: {
          enabled: this.enableTooltip,
          formatter() {
            const weekDays = [
              'Sonntag',
              'Montag',
              'Dienstag',
              'Mittwoch',
              'Donnerstag',
              'Freitag',
              'Samstag',
            ];
            const chartHoverDate = new Date(this.x);
            const chartHoverDay = weekDays[chartHoverDate.getDay()];
            const chartHoverUTCDate = new Date(
              Date.UTC(
                chartHoverDate.getFullYear(),
                chartHoverDate.getMonth(),
                chartHoverDate.getDate()
              )
            );
            const chartHoverDayNumber = chartHoverUTCDate.getUTCDay() || 7;

            const utcDateFromFirstWeek = new Date(
              chartHoverUTCDate.setUTCDate(
                chartHoverUTCDate.getUTCDate() + 4 - chartHoverDayNumber
              )
            );
            const yearStart = new Date(
              Date.UTC(utcDateFromFirstWeek.getUTCFullYear(), 0, 1)
            ).getTime();
            const totalWeekNumber = Math.ceil(
              ((chartHoverUTCDate.getTime() - yearStart) / 86400000 + 1) / 7
            );
            return (
              '<b>' +
              'Zeitstempel: ' +
              Highcharts.dateFormat(
                '%e.%m.%Y %H:%M',
                new Date(
                  new Date(this.x) -
                    new Date(this.x).getTimezoneOffset() * 60 * 1000
                )
              ) +
              '<br>Kalenderwoche: ' +
              totalWeekNumber +
              '<br>Tag: ' +
              chartHoverDay +
              '<br>Wert: ' +
              this.y +
              '</b>'
            );
          },
        },
        xAxis: {
          min: this.min,
          max: this.max,
          plotBands: this.xAxisPlotBands,
          type: 'datetime',
          labels: {
            enabled: this.enableXAxiesLabels,
            style: this.axisStyleFontSize,
          },
        },
        yAxis: {
          title: null,
          labels: {
            enabled: this.enableYAxiesLabels,
            style: {
              fontSize: this.axisStyleFontSize,
            },
          },
        },
        legend: {
          enabled: this.enableLegend,
        },
        plotOptions: {
          series: {
            stickyTracking: false,
            allowPointSelect: true,
            boostThreshold: 1,
            point: {
              events: {
                select: function () {
                  this.update({
                    groupId: 'edit',
                  });
                },
                unselect: function () {
                  this.update({
                    groupId: null,
                  });
                },
                drop: function (event) {
                  // get new values from Drag and it will be object of objects
                  const newDragPoints = Object.entries(event.newPoints).reduce(
                    (previous, [objectKey, objectValue]) => [
                      ...previous,
                      objectValue.point.options,
                    ],
                    []
                  );

                  const newDragPointKeys = newDragPoints.map(
                    (point) => point.x
                  );
                  //store new drag points value and also if point gets update again then remove it and again concat that
                  vm.dragPointsArray = vm.dragPointsArray
                    .filter((point) => !newDragPointKeys.includes(point.x))
                    .concat(newDragPoints);

                  // add overlay on chart calculation and add save buttons for the drag new points
                  if (!vm.isDataChangedByDrag) {
                    vm.$emit('setOperationOverlay', true);
                    vm.toggleDataChangeByDrag(true);
                    vm.addDragButtonsToChart();
                  }
                },
              },
            },
            lineColor: this.lineColor,
          },
          area: {
            fillColor: {
              linearGradient: {
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 1,
              },
              stops: [
                [0, Highcharts.Color('#adb9d2').setOpacity(1).get('rgba')],
                [1, Highcharts.Color('#adb9d2').setOpacity(0).get('rgba')],
              ],
            },
            animation: this.enableAnimation,
            marker: {
              enabled: false,
            },
            lineWidth: this.lineWidth,
            states: {
              hover: {
                lineWidth: this.lineWidth,
              },
            },
            threshold: null,
          },
        },
        line: {
          cursor: 'ns-resize',
        },
        navigation: {
          buttonOptions: {
            enabled: false,
          },
        },
        series: [...this.chartSeries],
      });
      this.loaded = true;
    },
    redraw() {
      this.chart.series[0].setData(
        JSON.parse(JSON.stringify(this.seriesDetails[0].data)),
        true
      );
    },
    updateChartWithPlotBands(newValues) {
      const addEventsToPlotBonds = newValues.map((plotBand) => ({
        ...plotBand,
        events: {
          click: function () {
            this.axis.chart.xAxis[0].setExtremes(plotBand.from, plotBand.to);
            this.axis.chart.showResetZoom();
          },
        },
      }));
      this.chart.update({
        xAxis: {
          plotBands: addEventsToPlotBonds,
        },
      });
    },
    addDraggingToChart(chart) {
      // update chart with the dragging functionality
      if (chart) {
        chart.update({
          plotOptions: {
            series: {
              boostThreshold: 0,
              dragDrop: {
                draggableY: true,
                groupBy: 'groupId',
              },
            },
          },
        });
      }
    },
    removeDraggingToChart() {
      // remove draggin from the chart
      if (this.chart) {
        this.chart.update({
          plotOptions: {
            series: {
              dragDrop: {
                draggableY: false,
              },
            },
          },
        });
        this.toggleDataChangeByDrag(false);
      }
    },
    addDragButtonsToChart() {
      //add custom buttons for the save or reset chart drag values
      if (this.chart) {
        this.chart.update({
          navigation: {
            buttonOptions: {
              enabled: true,
            },
          },
          exporting: {
            buttons: [
              {
                text: 'Anpassung übernehmen',
                onclick: () => this.saveChartWithDragValue(),
                verticalAlign: 'top',
                y: 0,
                x: -245,
                theme: {
                  height: 20,
                  'stroke-width': 1,
                  stroke: '#cccccc',
                },
              },
              {
                text: 'Anpassung verwerfen',
                onclick: () => this.resetChartWithoutDragValue(),
                verticalAlign: 'top',
                y: 0,
                x: -100,
                theme: {
                  height: 20,
                  'stroke-width': 1,
                  stroke: '#cccccc',
                },
              },
            ],
          },
        });
      }
    },
    removeDragButtonsToChart() {
      //remove buttons from the chart
      if (this.chart) {
        this.chart.update({
          exporting: {
            buttons: [],
          },
          navigation: {
            buttonOptions: {
              enabled: false,
            },
          },
        });
      }
    },
    resetChartWithoutDragValue() {
      //reset chart with the original value
      if (this.chart) {
        this.chart.series[0].update(
          {
            data: [...this.cloneOfChartSeries],
          },
          false
        );
        //reset drag points array
        this.dragPointsArray = [];
        //toggle overlay above the calculation
        this.$emit('setOperationOverlay', false);
        this.toggleDataChangeByDrag(false);
        this.chart.redraw();
        this.removeDragButtonsToChart();
      }
    },
    async saveChartWithDragValue() {
      const oldNewDataPointsValueForLog = [];
      if (this.chart) {
        const newDragPointKeys = this.dragPointsArray.map((point) => point.x);
        //get new chart data from the original data and dragged point data
        const newChartData = this.cloneOfChartSeries.map((dataPoint) => {
          if (newDragPointKeys.includes(dataPoint[0])) {
            const newDataPointValue = this.dragPointsArray.find(
              (draggedPoint) => draggedPoint.x === dataPoint[0]
            ).y;
            oldNewDataPointsValueForLog.push({
              timestamp: dataPoint[0],
              'old-value': dataPoint[1],
              'new-value': newDataPointValue,
            });
            return [dataPoint[0], Number(newDataPointValue.toFixed(10))];
          } else return dataPoint;
        });
        //calculate metadata for the new chart data
        const calculateMetadataResponse = await this.calculateMetaData({
          ...this.chartSeries[0],
          data: newChartData,
        });
        if (calculateMetadataResponse) {
          //increse the index for  new series
          this.addNewSeries({
            ...calculateMetadataResponse,
            data: newChartData,
          });
          //take a new clone of new chart data for the redo undo functionality
          this.cloneOfChartSeries = [...newChartData];

          //set new datapoint drag data for the logs on save
          this.$emit('setDragDataPoints', oldNewDataPointsValueForLog);
          this.dragPointsArray = [];
          this.$emit('setOperationOverlay', false);
          this.toggleDataChangeByDrag(false);
          this.removeDragButtonsToChart();
        } else {
          this.$emit('setOperationOverlay', false);
        }
      }
    },
    toggleDataChangeByDrag(dragValue) {
      this.isDataChangedByDrag = dragValue;
    },
    checkAllowDraggingToChart(event, chart) {
      let min, max;
      // Get min & max from xAxis
      min = event.xAxis[0].min;
      max = event.xAxis[0].max;
      if (chart.series[0].closestPointRangePx) {
        setTimeout(() => {
          //only add dragging point functionality if chart range is less then 150000000 otherwise it will affect the zoom
          if (this.chartSeries.length === 1) {
            if (chart.series[0].closestPointRangePx > 6) {
              this.addDraggingToChart(chart);
            } else {
              this.removeDraggingToChart();
            }
          }
        }, 500);
      }
      return [min, max];
    },
  },
  computed: {
    liveChart() {
      return this.chart.yAxis[0];
    },
    min() {
      let dates = [];
      for (let series of this.seriesDetails) {
        if (series.data) {
          dates.push(series.data[0][0]);
        }
      }
      const minNumber = Math.min(...dates);
      this.$emit('setMinimumChartValue', minNumber);
      return minNumber;
    },
    max() {
      let dates = [];
      for (let series of this.seriesDetails) {
        if (series.data) {
          let lengthSeries = series.data.length;
          dates.push(series.data[lengthSeries - 1][0]);
        }
      }
      const maxNumber = Math.max(...dates);
      this.$emit('setMaximumChartValue', maxNumber);
      return Number(maxNumber);
    },
  },
  created() {
    window.moment = Moment;
    MomentTimeZone();
  },
  mounted() {
    // Make sure chart input is always array
    //Make sure you clone the original chart data because highchart will update your data which you pass in chart series
    if (!Array.isArray(this.seriesDetails)) {
      this.cloneOfChartSeries = [...this.seriesDetails.data];
      this.chartSeries = [
        { ...this.seriesDetails, data: [...this.cloneOfChartSeries] },
      ];
    } else {
      if (this.seriesDetails.length > 1) this.chartSeries = this.seriesDetails;
      else {
        this.cloneOfChartSeries = [...this.seriesDetails[0].data];
        this.chartSeries = [
          { ...this.seriesDetails[0], data: [...this.cloneOfChartSeries] },
        ];
      }
    }
    this.setChart();
  },
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
  },
  watch: {
    redrawChart() {
      // Redraw chart
      if (this.redrawChart) this.redraw();
    },
    /*
    xAxisPlotBands(newValue) {
      this.updateChartWithPlotBands(newValue);
    },
    */
  },
};
</script>
